import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  InputField, CardInput, PaymentIcon, ErrorMessage,
} from 'anf-core-react';
import { useFormContext, Controller } from 'react-hook-form';
import { formatCardNumber, DEFAULT_ICON, DEFAULT_MAX_LENGTH } from './helpers/formatCard';
import { isValidCardNumber } from './helpers/cardValidations';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import useSwitchesFromContext from '../../../hooks/useSwitchesFromContext';

function CardNumberV1({
  cardNumber,
  label,
  errorMessage,
  index,
  onChange,
  cardTypesList,
  hasValidationError,
}) {
  const [iconName, setIconName] = useState(DEFAULT_ICON);
  const [maxLength, setMaxLength] = useState(DEFAULT_MAX_LENGTH);
  const [isInvalid, setIsInvalid] = useState(false);

  useEffect(() => {
    setIsInvalid(!!hasValidationError);
  }, [hasValidationError]);

  const handleChange = (event) => {
    const input = event.target.value;
    const formattedInput = formatCardNumber(input, cardTypesList);
    setIconName(formattedInput.type ? formattedInput.type : DEFAULT_ICON);
    setMaxLength(formattedInput.maxlength ? formattedInput.maxlength : DEFAULT_MAX_LENGTH);

    onChange(formattedInput);

    return formattedInput.formattedNumber;
  };

  const validateChange = (event) => {
    const input = event.target.value;
    setIsInvalid(!isValidCardNumber(input));
  };

  return (
    <CardInput
      paymentIcon={(
        <PaymentIcon
          iconName={iconName}
          labelText={null}
        />
      )}
      inputField={(
        <InputField
          id={`card-input-${index}`}
          label={label}
          name={`creditCard[${index}].cardNumber`}
          onChange={handleChange}
          onBlur={validateChange}
          type="tel"
          value={cardNumber}
          maxLength={maxLength}
          isInvalid={isInvalid}
          autoComplete=""
          classList="card-input"
          inputProps={{
            'data-validationrule': 'payment',
            'data-testid': `card-input-${index}`,
          }}
        >
          <ErrorMessage id="card-number-error-message">
            {errorMessage}
          </ErrorMessage>
        </InputField>
      )}
    />
  );
}

CardNumberV1.propTypes = {
  label: PropTypes.string.isRequired,
  errorMessage: PropTypes.string.isRequired,
  cardNumber: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  cardTypesList: PropTypes.arrayOf(PropTypes.shape({
    type: PropTypes.string.isRequired,
    maxlength: PropTypes.number.isRequired,
    code: PropTypes.string.isRequired,
    regex: PropTypes.string.isRequired,
  })).isRequired,
  hasValidationError: PropTypes.bool.isRequired,
};
function CardNumberV2({ index, hasValidationError }) {
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const {
    control, setError, trigger, watch,
  } = useFormContext();

  const cardNumberFieldName = `creditCard[${index}].cardNumber`;
  const cardNumberFieldValue = watch(cardNumberFieldName);

  const { paymentConfig: { creditCard: creditCardConfig } = {} } = checkoutPageState || {};
  const label = creditCardConfig?.cardNumberLabel?.value || '';
  const cardTypesList = creditCardConfig?.creditCardTypes;
  const creditCardErrorMessage = creditCardConfig?.creditCardErrorMessage?.value || '';
  const creditCardMissingErrorMessage = creditCardConfig?.creditCardMissingErrorMessage?.value || '';

  useEffect(() => {
    if (hasValidationError) {
      setError(cardNumberFieldName, {
        type: 'manual',
      });
    }
  }, [cardNumberFieldName, hasValidationError, setError]);

  const { iconName, maxLength } = useMemo(() => {
    if (!cardNumberFieldValue) {
      return { type: DEFAULT_ICON, maxlength: DEFAULT_MAX_LENGTH };
    }
    const formattedCardNumber = formatCardNumber(cardNumberFieldValue, cardTypesList);
    return {
      iconName: formattedCardNumber.type ? formattedCardNumber.type : DEFAULT_ICON,
      maxLength: formattedCardNumber.maxlength ? formattedCardNumber.maxlength : DEFAULT_MAX_LENGTH,
    };
  }, [cardNumberFieldValue, cardTypesList]);

  const validCardNumberFn = useCallback(
    (value) => isValidCardNumber(value) || (value?.length
      ? creditCardErrorMessage : creditCardMissingErrorMessage),
    [creditCardErrorMessage, creditCardMissingErrorMessage],
  );

  return (
    <CardInput
      paymentIcon={(
        <PaymentIcon
          iconName={iconName}
          labelText={null}
        />
      )}
      inputField={(
        <Controller
          name={cardNumberFieldName}
          control={control}
          defaultValue=""
          rules={{
            required: creditCardMissingErrorMessage,
            validate: validCardNumberFn,
          }}
          render={({ field, fieldState }) => (
            <InputField
              id={`card-input-${index}`}
              type="tel"
              label={label}
              onBlur={trigger}
              maxLength={maxLength}
              isInvalid={Boolean(fieldState.error)}
              autoComplete=""
              classList="card-input"
              inputProps={{
                'data-validationrule': 'payment',
                'data-testid': `card-input-${index}`,
              }}
              {...field}
              onChange={(event) => {
                const formattedCardNumber = formatCardNumber(event.target.value, cardTypesList);
                field.onChange(formattedCardNumber?.formattedNumber || '');
              }}
            >
              {fieldState.error ? (
                <ErrorMessage id="card-number-error-message">
                  {fieldState.error.message}
                </ErrorMessage>
              ) : null}
            </InputField>
          )}
        />
      )}
    />
  );
}

CardNumberV2.defaultProps = {
  index: 0,
};

CardNumberV2.propTypes = {
  index: PropTypes.number,
  hasValidationError: PropTypes.bool.isRequired,
};

export default function CardNumber(props) {
  const { getSwitchValue } = useSwitchesFromContext();
  const chkUsePaymentFieldsV2 = getSwitchValue('chkUsePaymentFieldsV2');
  if (chkUsePaymentFieldsV2) {
    return <CardNumberV2 {...props} />;
  }
  return <CardNumberV1 {...props} />;
}
